import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Send, User, Phone, FileText, Loader2, ExternalLink, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  {
    id: "welcome",
    name: "Welcome",
    template: "Hi [name]! Welcome to [company]. We received your enquiry and will contact you shortly.\nReply STOP to unsubscribe."
  },
  {
    id: "follow_up",
    name: "Follow Up",
    template: "Hi [name]! Following up on your enquiry about [product]. Are you still interested?\nReply YES to know more."
  },
  {
    id: "document_share",
    name: "Document Share",
    template: "Hi [name]! Please find the document you requested:\n[document_link]\nLet us know if you have questions."
  },
  {
    id: "meeting_reminder",
    name: "Meeting Reminder",
    template: "Hi [name]! Reminder about your meeting tomorrow at [time].\nReply CONFIRM to confirm or RESCHEDULE to change time."
  },
  {
    id: "course_promotion",
    name: "Course Promotion",
    template: "Hi [name]! Special offer on [course_name] - [discount]% off!\nEnroll now: [link]\nOffer valid till [date]."
  },
  {
    id: "insurance_renewal",
    name: "Insurance Renewal",
    template: "Hi [name]! Your insurance policy is due for renewal on [date].\nContact us to renew: [phone] or reply here."
  }
];

const WhatsAppComposeModal = ({ isOpen, onClose, lead }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('welcome');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSent(false);
      setUseCustom(false);
    }
  }, [isOpen]);

  if (!isOpen || !lead) return null;

  const formatPhone = (phone) => {
    if (!phone) return 'No phone number';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 10) {
      return `+91 ${clean.slice(0, 5)} ${clean.slice(5)}`;
    }
    return phone;
  };

  const getPreviewMessage = () => {
    if (useCustom) return customMessage;
    const template = TEMPLATES.find(t => t.id === selectedTemplate);
    let msg = template?.template || '';
    msg = msg.replace('[name]', lead.name || 'there');
    msg = msg.replace('[company]', 'AILeadMantra');
    msg = msg.replace('[product]', lead.tags?.[0] || 'your enquiry');
    return msg;
  };

  const sendMessage = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: lead.id,
          template_id: useCustom ? null : selectedTemplate,
          custom_message: useCustom ? customMessage : null,
          variables: {
            company: 'AILeadMantra'
          }
        })
      });

      if (response.ok) {
        setSent(true);
        toast.success('WhatsApp message sent successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openWhatsAppLink = () => {
    const phone = lead.phone?.replace(/\D/g, '');
    const formattedPhone = phone?.length === 10 ? `91${phone}` : phone;
    const message = encodeURIComponent(getPreviewMessage());
    window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
    toast.success('Opened WhatsApp');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md overflow-hidden"
          data-testid="whatsapp-compose-modal"
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-700 bg-green-900/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Send WhatsApp</h3>
                  <p className="text-green-300/70 text-sm">to {lead.name}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {sent ? (
            /* Success View */
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Message Sent!</h4>
              <p className="text-gray-400 mb-6">
                WhatsApp message sent to {lead.name}
              </p>
              <Button onClick={onClose} className="bg-green-600 hover:bg-green-500">
                Done
              </Button>
            </div>
          ) : (
            /* Compose View */
            <div className="p-4 space-y-4">
              {/* Lead Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold">
                  {lead.name?.charAt(0) || 'L'}
                </div>
                <div>
                  <p className="font-medium text-white">{lead.name}</p>
                  <p className="text-sm text-gray-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {formatPhone(lead.phone)}
                  </p>
                </div>
              </div>

              {/* Template or Custom Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUseCustom(false)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !useCustom 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-1" />
                  Template
                </button>
                <button
                  onClick={() => setUseCustom(true)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    useCustom 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  Custom Message
                </button>
              </div>

              {useCustom ? (
                /* Custom Message */
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Your Message</label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={4}
                    placeholder="Type your message..."
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-green-500 resize-none"
                  />
                </div>
              ) : (
                /* Template Selection */
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Select Template</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplate(t.id)}
                        className={`p-2 rounded-lg border text-left text-sm transition-all ${
                          selectedTemplate === t.id
                            ? 'bg-green-500/20 border-green-500 text-white'
                            : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-600'
                        }`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-2">Message Preview:</p>
                <p className="text-sm text-gray-300 whitespace-pre-line">
                  {getPreviewMessage() || 'Enter a message...'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={openWhatsAppLink}
                  variant="outline"
                  className="flex-1 border-green-700/50 text-green-300"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  wa.me
                </Button>
                <Button
                  onClick={sendMessage}
                  disabled={sending || (!useCustom && !selectedTemplate) || (useCustom && !customMessage)}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Message
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WhatsAppComposeModal;
